/**
 * Thunder, a few seconds behind its stroke (02 §5).
 *
 * The delay IS the distance - it is the only thing that tells you whether the storm is on top of
 * you or miles off, and it is the whole character of the sound. A clap that arrived with the flash
 * would read as an explosion; one that arrives three seconds later reads as weather. So the delay
 * is drawn per stroke, and what comes back is quieter the longer it took to get here.
 *
 * Deterministic, from the mission's own seed and the tick the stroke fell on - the same storm
 * sounds the same on every machine and in a replay, with no state to fall out of step. Pure but for
 * the callback that actually plays it.
 */

import { lightning, type Sky } from '../sim/weather';

/** Seconds between the flash and the clap: about 200 m to a kilometre away. */
const NEAR_SECONDS = 0.6;
const FAR_SECONDS = 3.2;
/** What a clap is worth at each end of that: the far ones are a rumble under the rain. */
const NEAR_GAIN = 1;
const FAR_GAIN = 0.45;

/** How far off this stroke was, 0 (overhead) to 1 (miles). Deterministic. Pure. */
export function strokeDistance(seed: number, strokeTick: number): number {
  const salt = Math.imul((seed ^ 0x9e37) + strokeTick, 1664525) + 1013904223;
  return ((salt >>> 8) & 0xffff) / 0xffff;
}

/** Seconds to wait, and how loud it will be when it lands. Pure. */
export function thunderAt(seed: number, strokeTick: number): { delay: number; gain: number } {
  const far = strokeDistance(seed, strokeTick);
  return {
    delay: NEAR_SECONDS + (FAR_SECONDS - NEAR_SECONDS) * far,
    gain: NEAR_GAIN + (FAR_GAIN - NEAR_GAIN) * far,
  };
}

/** ROUND5: the thunderstorm weather throws the stroke, so the biome is no longer part of the answer. */
export interface ThunderSky {
  sky: Sky;
  seed: number;
}

/** Claps still on their way here. */
interface Pending {
  left: number;
  gain: number;
}

export class Thunder {
  private pending: Pending[] = [];
  private lit = false;

  /** A new mission: nothing is on its way from the last one. */
  reset(): void {
    this.pending = [];
    this.lit = false;
  }

  /**
   * Watch for a stroke and let the ones already travelling arrive. `play` is called with a gain
   * scale when a clap lands.
   */
  step(where: ThunderSky, tick: number, dt: number, play: (gain: number) => void): void {
    const flash = lightning(where.sky, where.seed, tick) > 0;
    // The rising edge only: a stroke lasts a few ticks and must not clap on every one of them.
    if (flash && !this.lit) {
      const clap = thunderAt(where.seed, tick);
      this.pending.push({ left: clap.delay, gain: clap.gain });
    }
    this.lit = flash;

    for (let i = this.pending.length - 1; i >= 0; i--) {
      const clap = this.pending[i];
      if (!clap) continue;
      clap.left -= dt;
      if (clap.left > 0) continue;
      this.pending.splice(i, 1);
      play(clap.gain);
    }
  }
}
